import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { EASE } from "@/components/Reveal";
import { waLink } from "@/config";

const SERVICES = ["Data Analytics & Data Cleaning", "Excel Reports & Dashboards", "Power BI Dashboards", "Website Design & Development", "Logo Design & Branding", "AI Video & Advertisement Creation", "Social Media Content Creation", "Digital Marketing", "Other Services"];
const SESSION_KEY = "analytrova_lead_popup_closed";

const EMPTY = { name: "", phone: "", email: "", business: "", service: "", message: "" };

const buildWaMessage = (f) =>
    [
        "New Enquiry from the Analytrova website:",
        f.name && `Full Name: ${f.name}`,
        f.business && `Business Name: ${f.business}`,
        f.phone && `Phone Number: ${f.phone}`,
        f.email && `Email: ${f.email}`,
        f.service && `Service Interested In: ${f.service}`,
        f.message && `Message: ${f.message}`,
    ]
        .filter(Boolean)
        .join("\n");

export default function LeadPopup() {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [done, setDone] = useState(false);
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState(EMPTY);

    useEffect(() => {
        if (sessionStorage.getItem(SESSION_KEY)) return;
        const t = setTimeout(() => {
            setMounted(true);
            setVisible(true);
        }, 700);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const opener = () => {
            setDone(false);
            setMounted(true);
            setVisible(true);
        };
        window.addEventListener("analytrova:open-popup", opener);
        return () => window.removeEventListener("analytrova:open-popup", opener);
    }, []);

    const close = () => {
        sessionStorage.setItem(SESSION_KEY, "closed");
        setVisible(false);
        setTimeout(() => setMounted(false), 350);
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, form);
            setDone(true);
            toast.success("Thank you! Our team will reach out within 24 hours.");
            window.open(waLink(buildWaMessage(form)), "_blank");
            setTimeout(close, 6000);
        } catch (err) {
            console.error(err);
            toast.error("Submission failed — please try again, or reach us on WhatsApp.");
        } finally {
            setSending(false);
        }
    };

    if (!mounted) return null;

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-6 bg-burgundy/60 backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={close}
                    data-testid="lead-popup-overlay"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.97 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="relative w-full max-w-md bg-cream-soft border border-gold-deep/40 rounded-2xl shadow-[0_40px_90px_-30px_rgba(0,0,0,0.5)] max-h-[92dvh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="lead-popup"
                    >
                        <button
                            onClick={close}
                            aria-label="Close popup"
                            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full border border-burgundy/20 text-burgundy flex items-center justify-center hover:bg-burgundy hover:text-cream transition-colors"
                            data-testid="popup-close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="p-7 sm:p-9">
                            {done ? (
                                <div className="text-center py-8" data-testid="popup-success">
                                    <CheckCircle2 className="w-14 h-14 text-olive-deep mx-auto" strokeWidth={1.5} />
                                    <h3 className="mt-5 text-2xl text-charcoal">
                                        Thank you{form.name ? `, ${form.name.trim().split(" ")[0]}` : ""}!
                                    </h3>
                                    <p className="mt-2 text-sm text-charcoal/65">
                                        Your details are with our team — we'll reach out within 24 hours.
                                    </p>
                                    <button onClick={close} className="btn btn-burgundy mt-7" data-testid="popup-continue">
                                        Continue to Website
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <h3 className="text-[26px] sm:text-3xl leading-tight text-charcoal">
                                        Let's Grow Your Business Together!
                                    </h3>
                                    <p className="mt-2.5 text-sm text-charcoal/65">
                                        Tell us a little about your business, and our team will help you find the
                                        right solution.
                                    </p>

                                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="lead-form">
                                        <div>
                                            <label className="field-label" htmlFor="lp-name">Full Name</label>
                                            <input id="lp-name" className="field" required value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="popup-name" />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="field-label" htmlFor="lp-phone">Phone Number</label>
                                                <input id="lp-phone" type="tel" className="field" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="popup-phone" />
                                            </div>
                                            <div>
                                                <label className="field-label" htmlFor="lp-email">Email Address</label>
                                                <input id="lp-email" type="email" className="field" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="popup-email" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="field-label" htmlFor="lp-business">Business Name</label>
                                            <input id="lp-business" className="field" required value={form.business} onChange={set("business")} placeholder="Your business name" data-testid="popup-business" />
                                        </div>
                                        <div>
                                            <label className="field-label" htmlFor="lp-service">What service are you interested in?</label>
                                            <select id="lp-service" className="field" required value={form.service} onChange={set("service")} data-testid="popup-service">
                                                <option value="" disabled>Select a service</option>
                                                {SERVICES.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="field-label" htmlFor="lp-message">Message (optional)</label>
                                            <textarea id="lp-message" rows="3" className="field resize-none" value={form.message} onChange={set("message")} placeholder="Tell us about your goals..." data-testid="popup-message" />
                                        </div>
                                        <button type="submit" className="btn btn-burgundy w-full justify-center" disabled={sending} data-testid="popup-submit">
                                            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                                            {sending ? "Sending..." : "Get Started"}
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
